import {autobind} from 'core-decorators';
import {getDOMNodes, getDOMNode} from 'utils';
import BaseAdapter from './base';

@autobind
export default class JWPlayerAdapter extends BaseAdapter {
    // Reference: https://developer.jwplayer.com/jw-player/docs/developer-guide/api/javascript_api_reference/

    static findVideos() {
        // eslint-disable-next-line max-len
        const nodes = getDOMNodes('script[src*="//cdn.jwplayer.com/players/"], script[src*="//content.jwplatform.com/players/"]');
        return nodes.map(script => {
            const matches = script.getAttribute('src').match(/\/players\/([\w-]+)-([\w-]+)/);
            return {
                provider: 'jwplayer',
                embedType: 'Platform JS Embed',
                videoId: matches[1],
                playerId: matches[2],
                entryNode: script,
            };
        });
    }

    initialize(options) {
        this.playerId = options.playerId;
        return super.initialize(options);
    }

    getVideoDimensions() {
        const width = this.videoNode.clientWidth || 0;
        const height = this.videoNode.clientHeight || 0;
        return [width, height];
    }

    loadAPI() {
        return new Promise((resolve, reject) => {
            const check = () => {
                // Wait for JWPlayer to initialize
                const videoNode = getDOMNode(`[id*="${this.videoId}_${this.playerId}"][class*="jwplayer"]`);
                if (window.jwplayer && videoNode) {
                    this.videoNode = videoNode;
                    resolve();
                } else {
                    setTimeout(check, BaseAdapter.CHECK_INTERVAL);
                }
            };
            check();

            const timeout = () => reject('JWPLayer API failed to load');
            setTimeout(timeout, BaseAdapter.API_TIMEOUT);
        });
    }

    attach() {
        return new Promise(resolve => {
            this.api = window.jwplayer(this.videoNode);

            this.api.on('buffer', () => this.setPlayerState(BaseAdapter.STATES.Buffering));
            this.api.on('pause', () => this.setPlayerState(BaseAdapter.STATES.Paused));
            this.api.on('play', () => this.setPlayerState(BaseAdapter.STATES.Playing));
            this.api.on('seek', () => this.setPlayerState(BaseAdapter.STATES.Seek));
            this.api.on('complete', () => this.setPlayerState(BaseAdapter.STATES.Ended));
            this.api.on('time', this.syncPlayhead);

            // Note: The documentation specifies that the API is ready to be used only after the 'ready' event has
            // been fired but that event is not getting fired so we assume we're good to go and skip the waiting.
            // The original coode should be: this.api.on('ready', this.handleReady);

            this.handleReady();

            resolve();
        });
    }

    handleReady() {
        this.setDuration(this.getDuration());
        this.api.setCurrentCaptions(0);
    }

    syncPlayhead(payload) {
        const currentTime = this.toMS(payload.position);
        this.setPlayhead(currentTime);
    }

    seek(time) {
        this.api.seek(this.toSS(time));
    }

    pause() {
        if (this.isPlaying()) this.api.pause(true);
    }

    isPlaying() {
        return this.api.getState() === 'playing';
    }

    getDuration() {
        return this.toMS(this.api.getDuration());
    }

    getCurrentTime() {
        return this.toMS(this.api.getCurrentTime());
    }

    getVideoTitle() {
        return new Promise(resolve => {
            const video = this.api.getPlaylistItem();
            resolve(video.title);
        });
    }
}



// WEBPACK FOOTER //
// ./scripts/adapters/jwplayer.js